import { AnalyticsAvailabilityTypes, AnalyticsSizeGuideViewedTypes, isProduct, PDPVersion, ProductExcluded, RANGE_FILTER, SessionAuthorityTypes, } from '@as-react/common-types';
import { capitalize, determineSelectedSku, findColor, findSizeBySku, getCurrencyISOCode, getCustomWindowObject, getDefaultProductColor, isNullOrUndefined, isOneSizeProduct, mergeObjectDeep, } from '@as-react/common-utils';
import { setIn } from 'formik';
import { COMPARE_TRIGGERED_COMPONENT } from '../constants/CompareConstants';
import { FILTER_SIZE_SEPARATOR } from '../constants/GeneralConstants';
import { NOTHING_FOUND_SESSION_KEY } from '../constants/SearchConstants';
import { CompareActionTypes, ProductAddToBasketLocations, WishListActionTypes } from '../enums/ActionEnums';
import { DirectCallRules, EventActions, Events, PageId, ReturnPortalEventActionType, } from '../enums/AnalyticsEnums';
import { isAnalyticsDigitalPartnerEnrolmentAuthEmail, isAnalyticsDigitalPartnerEnrolmentAuthLogin, isAnalyticsDigitalPartnerEnrolmentAuthPasswordForgotten, isAnalyticsDigitalPartnerEnrolmentAuthRegister, isAnalyticsDigitalPartnerEnrolmentConfirmation, isAnalyticsDigitalPartnerEnrolmentEligibilityCheck, isAnalyticsDigitalPartnerEnrolmentLeave, isAnalyticsDigitalPartnerEnrolmentStart, } from '../redux/analytics/actions/AnalyticsActions.interfaces';
import { calculateDifferenceInMs, convertMillisecondsToDays } from '../util/DateUtil';
import SessionStorageUtil from './SessionStorageUtil';
const filterProductsFromListerItems = (listerItems) => listerItems.filter(item => isProduct(item));
const convertListerItemToProductImpression = (listerItems, opts) => listerItems.filter(isProduct).map((productItem, index) => {
    const selectedColorVariation = findColor(productItem.colours, productItem.colourId);
    return Object.assign(Object.assign(Object.assign(Object.assign({}, productItem.impression), { colourId: selectedColorVariation.colourId, colourCode: selectedColorVariation.colourCode }), (Boolean(opts === null || opts === void 0 ? void 0 : opts.withQuantity) && { quantity: 1 })), (Boolean(opts === null || opts === void 0 ? void 0 : opts.withPosition) && { position: index + 1 }));
});
const objectToString = (object) => Object.keys(object).join(',');
const subscriptionsObjectToString = (preferences) => Object.keys(preferences)
    .filter(key => Boolean(preferences[key]))
    .join(',');
const generateAdobeAnalyticsBundleProduct = (product, bundleProductType) => {
    var _a;
    const selectedColor = product.colours[0];
    const selectedSize = findSizeBySku(selectedColor.sizes, determineSelectedSku(selectedColor));
    const activeProductPrice = (selectedSize === null || selectedSize === void 0 ? void 0 : selectedSize.prices) || selectedColor.prices;
    return {
        attributes: {
            sellPrice: (_a = activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.SELL) === null || _a === void 0 ? void 0 : _a.original,
            RRPSTD: (activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.RRP)
                ? activeProductPrice.RRP.original
                : (activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.STD)
                    ? activeProductPrice.STD.original
                    : (activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.SELL)
                        ? activeProductPrice.SELL.original
                        : undefined,
            brand: product.brand,
            productRating: product.productRating,
            productReviews: product.productReviews,
            productImages: selectedColor.image ? 1 : 0,
            bundleProductType,
        },
        productInfo: {
            productID: product.productId,
            colorId: selectedColor,
            color: selectedColor.description,
            size: selectedSize === null || selectedSize === void 0 ? void 0 : selectedSize.originalSize,
            colorCode: selectedColor.colourCode,
        },
    };
};
export const generateAdobeAnalyticsAdditionalProductList = (additionalProducts) => additionalProducts.map(additionalProduct => {
    var _a;
    const { displayedColor, product } = additionalProduct;
    const selectedSize = findSizeBySku(displayedColor.sizes, determineSelectedSku(displayedColor));
    const activeProductPrice = (selectedSize === null || selectedSize === void 0 ? void 0 : selectedSize.prices) || displayedColor.prices;
    return {
        attributes: {
            sellPrice: (_a = activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.SELL) === null || _a === void 0 ? void 0 : _a.original,
            RRPSTD: (activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.RRP)
                ? activeProductPrice.RRP.original
                : (activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.STD)
                    ? activeProductPrice.STD.original
                    : (activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.SELL)
                        ? activeProductPrice.SELL.original
                        : undefined,
            brand: product.brand,
            productRating: product.productRating,
            productReviews: product.productReviews,
            productImages: displayedColor.image ? 1 : 0,
            additionalProductBundle: 'Additional',
        },
        productInfo: {
            productID: product.productId,
            colorId: displayedColor,
            color: displayedColor.description,
            colorCode: displayedColor.colourCode,
        },
    };
});
const generateProductGroupImpression = (product, productTilePosition) => {
    var _a, _b;
    const activeColor = getDefaultProductColor(product);
    return {
        productInfo: {
            productID: product.productId,
            colorCode: activeColor === null || activeColor === void 0 ? void 0 : activeColor.colourCode,
            color: activeColor === null || activeColor === void 0 ? void 0 : activeColor.description,
        },
        attributes: {
            sellPrice: (_b = (_a = activeColor === null || activeColor === void 0 ? void 0 : activeColor.prices) === null || _a === void 0 ? void 0 : _a.SELL) === null || _b === void 0 ? void 0 : _b.original,
            brand: product.brand,
            productTilePosition,
        },
    };
};
const generateAdobeAnalyticsBundleProductList = (bundleSlots) => bundleSlots.flatMap(slot => slot.bundleChildren.flatMap((child, index) => generateAdobeAnalyticsBundleProduct(child, index === 0 ? 'Child' : 'Alternative')));
const getSelectedColorForProduct = (product) => { var _a; return (_a = product.productColorVariations) === null || _a === void 0 ? void 0 : _a.find(color => color.colorId === product.selectedColorId); };
const getSelectedSizeForProduct = (product) => {
    const selectedColor = getSelectedColorForProduct(product);
    if (selectedColor) {
        return selectedColor.sizes.find(size => size.sku === product.selectedSku);
    }
    return undefined;
};
export const generateCriteoProductIds = (listerItems) => {
    const productItems = filterProductsFromListerItems(listerItems);
    const firstThreeProducts = productItems.length > 3 ? productItems.slice(0, 3) : productItems;
    return { ProductID: firstThreeProducts.map(product => product.productId) };
};
const userIsLoggedInHelper = (session) => { var _a, _b; return session.authorities && ((_b = (_a = session.authorities) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.authority) !== SessionAuthorityTypes.ANONYMOUS && !!session.amcUuid; };
export const generateCriteoUserData = (payload) => ({
    HashedEmail: userIsLoggedInHelper(payload) ? payload.amcUuid : 'anonymous',
});
export const generateCriteoProductId = (productId) => ({ ProductID: productId });
export const generateCriteoBasketData = (products) => ({
    ProductID: products.map(_product => ({
        id: _product.id,
        price: _product.price,
        quantity: _product.quantity,
    })),
});
export const generateCriteoImpressionEvent = (baseWindowObjectName, additionalParams) => {
    const criteoPageObject = getCustomWindowObject(baseWindowObjectName);
    const pageType = getCustomWindowObject('SITE.Config.analyticsPageType');
    if (!isNullOrUndefined(pageType) &&
        !isNullOrUndefined(criteoPageObject) &&
        !isNullOrUndefined(criteoPageObject.HashedEmail)) {
        const objectToSend = Object.assign(Object.assign(Object.assign({ TransactionID: undefined }, criteoPageObject), additionalParams), { event: 'criteodata', SiteType: /Mobi|Android/i.test(navigator.userAgent) ? 'm' : 'd', HashedEmail: criteoPageObject.HashedEmail === 'anonymous' ? '' : criteoPageObject.HashedEmail });
        switch (pageType) {
            case 'product':
            case 'cart':
            case 'brands':
            case 'search':
            case 'lister':
            case 'category':
                if (!isNullOrUndefined(objectToSend.ProductID)) {
                    return objectToSend;
                }
                break;
            case 'purchase':
                if (!isNullOrUndefined(objectToSend.ProductID) && !isNullOrUndefined(objectToSend.TransactionID)) {
                    return objectToSend;
                }
                break;
            case 'home':
                return objectToSend;
            default:
                break;
        }
    }
    return null;
};
export const generateCriteoProductImpressionEvent = (baseWindowObjectName, listerItems) => generateCriteoImpressionEvent(baseWindowObjectName, generateCriteoProductIds(listerItems));
export const generateCriteoProductIdsFromBasketProducts = (products) => ({
    ProductID: products.slice(0, 3).map(product => product.productId),
});
export const generateCriteoProductImpressionEventFromBasketProducts = (baseWindowObjectName, products) => {
    if (products) {
        generateCriteoImpressionEvent(baseWindowObjectName, generateCriteoProductIdsFromBasketProducts(products));
    }
};
export const generateCriteoImpressionEventFromConfirmation = (baseWindowObjectName, payload) => {
    var _a;
    return generateCriteoImpressionEvent(baseWindowObjectName, Object.assign({ TransactionID: (_a = payload.purchase.actionField) === null || _a === void 0 ? void 0 : _a.id }, generateCriteoBasketData(payload.purchase.products)));
};
export const generateGoogleAnalyticsProductImpressionEvent = (listerItems) => {
    const impressions = convertListerItemToProductImpression(listerItems !== null && listerItems !== void 0 ? listerItems : []);
    if (Array.isArray(impressions) && impressions.length > 0) {
        return {
            event: 'productImpression',
            ecommerce: impressions,
        };
    }
    return null;
};
export const generateAdobeAnalyticsProductSearchImpressionEvent = (searchTerm) => {
    if (searchTerm && searchTerm !== '') {
        return {
            page: {
                pageInfo: {
                    onsiteSearch: searchTerm,
                },
            },
        };
    }
    return null;
};
export const generateAdobeAnalyticsSetPartialSearchResults = () => ({
    page: {
        pageInfo: {
            partialResults: true,
        },
    },
});
export const generateAdobeAnalyticsFilterFacetImpression = (activeFilters) => {
    const maxAmountFilters = 5;
    const maxAmountFilterValues = 3;
    if (activeFilters) {
        const filters = {};
        Object.keys(activeFilters).forEach((filterKey, filterIndex) => {
            var _a, _b, _c, _d, _e;
            if (((_a = activeFilters[filterKey]) === null || _a === void 0 ? void 0 : _a.filterType) === RANGE_FILTER &&
                !isNullOrUndefined((_b = activeFilters[filterKey]) === null || _b === void 0 ? void 0 : _b.rangeMin) &&
                !isNullOrUndefined((_c = activeFilters[filterKey]) === null || _c === void 0 ? void 0 : _c.rangeMax) &&
                filterIndex < maxAmountFilters) {
                filters[`type${filterIndex + 1}`] = filterKey;
                filters[`type${filterIndex + 1}Value1`] = `#${activeFilters[filterKey].rangeMin}#-#${activeFilters[filterKey].rangeMax}#`;
            }
            else if (((_e = (_d = activeFilters[filterKey]) === null || _d === void 0 ? void 0 : _d.values) === null || _e === void 0 ? void 0 : _e.length) && filterIndex < maxAmountFilters) {
                filters[`type${filterIndex + 1}`] = filterKey;
                activeFilters[filterKey].values.forEach((filterValue, filterKeyIndex) => {
                    let filterName = filterValue.filterKeyName;
                    if (filterName.includes(FILTER_SIZE_SEPARATOR)) {
                        const [size, category] = filterName.split(FILTER_SIZE_SEPARATOR);
                        filterName = `${size}_${category}`;
                    }
                    if (filterKeyIndex < maxAmountFilterValues) {
                        filters[`type${filterIndex + 1}Value${filterKeyIndex + 1}`] = filterName;
                    }
                });
            }
        });
        return {
            page: { filters },
        };
    }
    return null;
};
export const generateGoogleAnalyticsBasketEvent = (data) => (Object.assign(Object.assign({}, data), { ecommerce: Object.keys(data.ecommerce).reduce((accumulator, currentValue) => {
        let dataToAdd = {};
        if (data.ecommerce[currentValue] !== null &&
            !(currentValue === 'impressions' && data.ecommerce[currentValue].length === 0)) {
            dataToAdd = {
                [currentValue]: data.ecommerce[currentValue],
            };
        }
        return Object.assign(Object.assign({}, accumulator), dataToAdd);
    }, {}) }));
const generateGAProductTypeFromProductDetail = (product) => {
    var _a;
    const selectedColorVariation = getSelectedColorForProduct(product);
    return {
        brand: product.brand ? product.brand.description : null,
        category: product.category ? product.category.description : null,
        id: product.productId,
        name: product.productTranslatedProperties && 'title' in product.productTranslatedProperties
            ? product.productTranslatedProperties.title
            : '',
        price: (_a = selectedColorVariation.prices) === null || _a === void 0 ? void 0 : _a.SELL.original,
        variant: selectedColorVariation.color,
        colourId: selectedColorVariation.colorId,
        colourCode: selectedColorVariation.colorCode,
        productColorId: selectedColorVariation.productColorId,
        quantity: 1,
        skuId: product.selectedSku,
    };
};
export const generateGoogleAnalyticsProductDetailEvent = (product, event, currency) => {
    return {
        event,
        ecommerce: Object.assign(Object.assign({}, (currency ? { currencyCode: getCurrencyISOCode(currency) } : {})), { detail: {
                actionField: {
                    list: null,
                },
                products: [generateGAProductTypeFromProductDetail(product)],
            } }),
    };
};
export const generateGoogleAnalyticsSizePickedEvent = (product, event) => {
    const selectedColor = getSelectedColorForProduct(product);
    return isOneSizeProduct(selectedColor.sizes, getCustomWindowObject('SITE.Config.product.code.oneSize'))
        ? null
        : generateGoogleAnalyticsProductDetailEvent(product, event);
};
export const generateGoogleAnalyticsCheckoutOptionEvent = (isNewCustomer) => ({
    event: 'checkoutOption',
    ecommerce: {
        checkout_option: {
            actionField: {
                step: 2,
                option: isNewCustomer ? 'New customer' : 'Existing customer',
            },
        },
    },
});
export const generateGoogleAnalyticsCheckoutStepEvent = ({ step, option = '' }) => ({
    event: 'checkout',
    ecommerce: {
        checkout_option: {
            actionField: {
                step,
                option,
            },
        },
    },
});
export const generateGoogleAnalyticsTransactionDataEvent = (data) => ({
    event: 'transactiondata',
    ecommerce: data,
});
export const generateAdobeAnalyticsBasketChangeEvent = (data) => {
    var _a;
    let userData = {};
    let productData = {};
    let pageData = {};
    let cartData = {};
    let eventData = {};
    let componentData = {};
    if (data.user) {
        userData = {
            user: {
                attributes: {
                    newsletterOptin: (_a = data.user.attributes) === null || _a === void 0 ? void 0 : _a.newsletterOptin,
                },
            },
        };
    }
    if (data.product) {
        productData = {
            product: data.product,
        };
    }
    if (data.page && data.page.attributes) {
        pageData = {
            page: {
                attributes: Object.assign(Object.assign({}, (!!data.page.attributes.primaryCategory && {
                    primaryCategory: data.page.attributes.primaryCategory,
                })), (!!data.page.attributes.addCardLocation && {
                    addCardLocation: data.page.attributes.addCardLocation,
                })),
            },
        };
    }
    if (data.cart && data.cart.cartID) {
        cartData = {
            cartID: data.cart.cartID,
        };
    }
    if (data.event) {
        eventData = {
            event: data.event,
        };
    }
    if (data.component) {
        componentData = {
            component: data.component,
        };
    }
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, userData), productData), pageData), cartData), eventData), componentData);
};
export const generateAdobeAnalyticsConfirmationPageEvent = (data) => {
    let productData = {};
    let pageData = {};
    let transactionData = {};
    let eventData = {};
    if (data.product) {
        productData = {
            product: data.product,
        };
    }
    if (data.page && data.page.attributes) {
        pageData = {
            page: {
                attributes: Object.assign({}, (!!data.page.attributes.primaryCategory && {
                    primaryCategory: data.page.attributes.primaryCategory,
                })),
            },
        };
    }
    if (data.transaction) {
        transactionData = {
            transaction: data.transaction,
        };
    }
    if (data.event) {
        eventData = {
            event: data.event,
        };
    }
    return Object.assign(Object.assign(Object.assign(Object.assign({}, productData), pageData), transactionData), eventData);
};
export const generateAdobeAnalyticsSetAddToBasketLocation = (addToBasketLocation) => ({
    page: {
        attributes: {
            addCardLocation: addToBasketLocation,
        },
    },
});
export const generateAdobeAnalyticsSetBasketToken = (token) => ({
    cart: {
        cartID: token,
    },
});
export const generateAdobeAnalyticsSetAddBundleToBasketEvent = (data) => {
    if (!!data && data.length > 1) {
        return {
            product: data,
        };
    }
    return null;
};
export const generateDigitalPartnerEnrolmentDigitalDataObject = (action) => {
    let pageObject = {};
    const pageObjectGenerator = [
        {
            checker: isAnalyticsDigitalPartnerEnrolmentStart,
            ruleNameSuffix: 'click to login',
        },
        {
            checker: isAnalyticsDigitalPartnerEnrolmentAuthEmail,
            pageInfo: {
                pageID: `${action.payload.partner}:0:email`,
                pageType: 'dpe member flow',
            },
            ruleNameSuffix: 'flow page',
        },
        {
            checker: isAnalyticsDigitalPartnerEnrolmentAuthLogin,
            pageInfo: {
                pageID: `${action.payload.partner}:1:login`,
                pageType: 'dpe member flow',
            },
            ruleNameSuffix: 'flow page',
        },
        {
            checker: isAnalyticsDigitalPartnerEnrolmentAuthRegister,
            pageInfo: {
                pageID: `${action.payload.partner}:1b:register`,
                pageType: 'dpe member flow',
            },
            ruleNameSuffix: 'flow page',
        },
        {
            checker: isAnalyticsDigitalPartnerEnrolmentAuthPasswordForgotten,
            pageInfo: {
                pageID: `${action.payload.partner}:1c:password_forgotten`,
                pageType: 'dpe member flow',
            },
            ruleNameSuffix: 'flow page',
        },
        {
            checker: isAnalyticsDigitalPartnerEnrolmentEligibilityCheck,
            pageInfo: {
                pageID: `${action.payload.partner}:2:active advantage`,
                pageType: 'dpe member flow',
            },
            ruleNameSuffix: 'flow page',
        },
        {
            checker: isAnalyticsDigitalPartnerEnrolmentConfirmation,
            pageInfo: {
                pageID: `${action.payload.partner}:3:confirmation`,
                pageType: 'dpe member flow',
            },
            ruleNameSuffix: 'flow page',
        },
        {
            checker: isAnalyticsDigitalPartnerEnrolmentLeave,
            pageInfo: {
                pageID: `${action.payload.partner}:leave`,
                pageType: 'dpe member flow',
            },
            ruleNameSuffix: 'leave page',
        },
    ];
    const selectedPageObjectGenerator = pageObjectGenerator.find(item => item.checker(action));
    if (selectedPageObjectGenerator) {
        pageObject = Object.assign(Object.assign({}, pageObject), { page: Object.assign(Object.assign({}, pageObject.page), { pageInfo: Object.assign({}, selectedPageObjectGenerator.pageInfo) }), event: [
                {
                    eventInfo: {
                        directCallRule: true,
                        ruleName: `${action.payload.partner.toString()}:${selectedPageObjectGenerator.ruleNameSuffix}`,
                        eventAction: '',
                    },
                },
            ] });
    }
    return pageObject;
};
export const generateFormErrorMessageDigitalDataObject = (errorMessage) => {
    let pageObject = null;
    const errorMessageFields = Object.keys(errorMessage);
    if (errorMessageFields.length > 0) {
        pageObject = {
            page: {
                attributes: {
                    errorMessageForms: errorMessageFields.map(field => `${field}:${errorMessage[field]}`).join('|'),
                },
            },
            event: [
                {
                    eventInfo: {
                        directCallRule: true,
                        ruleName: DirectCallRules.ERROR_MESSAGE,
                        eventAction: '',
                    },
                },
            ],
        };
    }
    return pageObject;
};
const getDeliveryPromiseForProduct = (product) => {
    if (product.deliveryPromise && product.deliveryPromise.deliveryPromises.length > 0) {
        const currentDate = new Date();
        const deliveryMinInMS = calculateDifferenceInMs(product.deliveryPromise.deliveryDateFrom, currentDate);
        const deliveryMinInDays = Math.ceil(convertMillisecondsToDays(deliveryMinInMS));
        const deliveryMaxInMS = calculateDifferenceInMs(product.deliveryPromise.deliveryDateTo, currentDate);
        const deliveryMaxInDays = Math.ceil(convertMillisecondsToDays(deliveryMaxInMS));
        return {
            deliveryMinDays: deliveryMinInDays,
            deliveryMaxDays: deliveryMaxInDays,
            deliveryPromises: product.deliveryPromise.deliveryPromises,
        };
    }
    return undefined;
};
const getPriceViewModelForProduct = (selectedColor, selectedSize) => {
    let price = undefined;
    if (selectedColor) {
        price = selectedColor.prices;
        if (selectedSize) {
            price = selectedSize.prices;
        }
    }
    return price;
};
const getExpertReviewForProduct = (product, reviews) => {
    const selectedReview = reviews.find(item => +item.colorId === product.selectedColorId && item.productCode === product.productCode);
    return selectedReview
        ? selectedReview
        : reviews.find(item => item.productCode === product.productCode && !item.colorId);
};
const generatePossibleProductDiscount = (selectedColor, selectedSize) => {
    let discountPercentage = undefined;
    if (selectedColor) {
        discountPercentage = selectedColor.colourDiscountPercentage;
        if (selectedSize) {
            discountPercentage = selectedSize.productDiscountPercentage;
        }
    }
    return discountPercentage;
};
const generateAdobeAnalyticsProduct = (product, options) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { expertReviews, pdpTemplateName, sizeGuideAvailabilityState, sizeGuideViewedSate, bundleProductType, inANutshellAvailability, additionalProductBundle, } = options;
    const selectedColor = getSelectedColorForProduct(product);
    const selectedSize = getSelectedSizeForProduct(product);
    const activeProductPrice = getPriceViewModelForProduct(selectedColor, selectedSize);
    const deliveryPromiseInfo = getDeliveryPromiseForProduct(product);
    const productData = {
        attributes: Object.assign(Object.assign({}, (additionalProductBundle && { additionalProductBundle })), { sellPrice: (_a = activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.SELL) === null || _a === void 0 ? void 0 : _a.original, RRPSTD: (activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.RRP)
                ? activeProductPrice.RRP.original
                : (activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.STD)
                    ? activeProductPrice.STD.original
                    : (activeProductPrice === null || activeProductPrice === void 0 ? void 0 : activeProductPrice.SELL)
                        ? activeProductPrice.SELL.original
                        : undefined, productPriceOverlay: (_b = selectedColor === null || selectedColor === void 0 ? void 0 : selectedColor.priceOverlay) === null || _b === void 0 ? void 0 : _b.overlayCode, productQualifier: product.productQualifier, brandValue: product.brandQualifier, buyingGroup: product.buyingGroup, gender: product.gender, brand: (_c = product.brand) === null || _c === void 0 ? void 0 : _c.description, productRating: (_d = product.productReview) === null || _d === void 0 ? void 0 : _d.avgScore, productReviews: (_e = product.productReview) === null || _e === void 0 ? void 0 : _e.numberOfScores, productDiscountPercentage: generatePossibleProductDiscount(selectedColor, selectedSize), productImages: ((_f = selectedColor === null || selectedColor === void 0 ? void 0 : selectedColor.images) === null || _f === void 0 ? void 0 : _f.length) || 0, lastPieces: (selectedSize === null || selectedSize === void 0 ? void 0 : selectedSize.lastPieces) || false }),
        productInfo: {
            productID: product.productId,
            colorId: product.selectedColorId,
            color: selectedColor.translations.description,
            size: selectedSize === null || selectedSize === void 0 ? void 0 : selectedSize.originalSize,
            colorCode: selectedColor.colorCode,
        },
        deliveryPromiseInfo,
    };
    if (inANutshellAvailability) {
        productData.attributes.inANutshellFeatures = inANutshellAvailability;
    }
    if (expertReviews === null || expertReviews === void 0 ? void 0 : expertReviews.length) {
        const review = getExpertReviewForProduct(product, expertReviews);
        if (review) {
            productData.productInfo.expertReview = true;
        }
    }
    if (pdpTemplateName) {
        productData.attributes.productTemplate = pdpTemplateName;
    }
    if (sizeGuideAvailabilityState) {
        productData.attributes.sizeTable = sizeGuideAvailabilityState;
    }
    if (sizeGuideViewedSate) {
        productData.attributes.productSizeTable = sizeGuideViewedSate;
    }
    if (product.pdpVersion === PDPVersion.V2) {
        productData.attributes.excludeHomeDelivery = ((_g = product.fulfillment) === null || _g === void 0 ? void 0 : _g.hdExcluded)
            ? ProductExcluded.HD_EXCLUDED
            : ProductExcluded.HD_NOT_EXCLUDED;
        productData.attributes.excludeClickAndCollect = ((_h = product.fulfillment) === null || _h === void 0 ? void 0 : _h.ccExcluded)
            ? ProductExcluded.CC_EXCLUDED
            : ProductExcluded.CC_NOT_EXCLUDED;
        productData.attributes.excludeStoreSourcing = (selectedSize === null || selectedSize === void 0 ? void 0 : selectedSize.storeSourceExcluded)
            ? ProductExcluded.SS_EXCLUDED
            : ProductExcluded.SS_NOT_EXCLUDED;
        productData.attributes.ourPlanet = product.ourPlanet ? 'ourPlanet' : 'standard';
    }
    if (bundleProductType) {
        productData.attributes.bundleProductType = bundleProductType;
    }
    return productData;
};
export const generateAdobeAnalyticsProductDetailImpression = (productState, directCallRule) => {
    var _a;
    return ({
        page: {
            pageInfo: {
                pageType: 'product',
            },
        },
        product: [generateAdobeAnalyticsProduct(productState.product, { expertReviews: (_a = productState.expertReview) === null || _a === void 0 ? void 0 : _a.reviews })],
        directCallRule: directCallRule,
    });
};
export const generateAdobeAnalyticsCookieConsentTriggers = (consentSettings, directCallRule) => {
    const userData = {
        consent: {},
    };
    Object.keys(consentSettings).forEach((consentGroup) => {
        userData.consent[consentGroup] = consentSettings[consentGroup];
    });
    return {
        user: userData,
        directCallRule: directCallRule,
    };
};
export const generateGoogleAnalyticsCookieConsentTriggers = (consentSettings) => {
    const googleData = {
        event: 'cookiesReady',
    };
    Object.keys(consentSettings).forEach((consentGroup) => {
        googleData[`userConsent${capitalize(consentGroup)}`] = consentSettings[consentGroup];
    });
    return googleData;
};
export const generateAdobeAnalyticsListerProductClick = () => ({
    directCallRule: DirectCallRules.LISTER_PRODUCT_CLICK,
});
export const generateAdobeAnalyticsFredhopperSegment = (segment) => ({
    page: {
        attributes: {
            fredhopperSegment: segment,
        },
    },
});
export const generateAdobeAnalyticsWishlistEvent = (action, prevState) => {
    var _a;
    const { isPDP, isWishlistPage, product: currentProduct } = action.payload;
    const isMainPDPProduct = isPDP
        ? Number(prevState.product.productId) === currentProduct.productId
        : false;
    let directCallRule = '';
    let product;
    let productData;
    if (action.type === WishListActionTypes.ANALYTICS_ADD) {
        directCallRule = DirectCallRules.WISHLIST_ADD;
    }
    else if (isPDP && isMainPDPProduct && action.type === WishListActionTypes.ANALYTICS_REMOVE) {
        directCallRule = DirectCallRules.WISHLIST_REMOVE_PDP;
    }
    else if (isWishlistPage && action.type === WishListActionTypes.ANALYTICS_REMOVE) {
        directCallRule = DirectCallRules.WISHLIST_REMOVE_WLIST;
    }
    else {
        directCallRule = DirectCallRules.WISHLIST_REMOVE;
    }
    if (isPDP && isMainPDPProduct) {
        product = prevState.product;
    }
    else if (isWishlistPage && prevState.wishList.items) {
        product = prevState.wishList.items.find((item) => item.productId === currentProduct.productId && item.colourId === currentProduct.colorId);
    }
    else if ('lister' in prevState && prevState.lister.items) {
        product = prevState.lister.items.find(item => 'productId' in item && item.productId === currentProduct.productId && item.colourId === currentProduct.colorId);
    }
    if (product) {
        productData = [
            {
                attributes: {
                    brand: isPDP ? (_a = product.brand) === null || _a === void 0 ? void 0 : _a.description : product.brand,
                    brandValue: '',
                    buyingGroup: isPDP ? product.buyingGroup : '',
                    gender: isPDP ? product.gender : product.genderCode,
                    productQualifier: isPDP ? product.productQualifier : '',
                },
                productInfo: {
                    productID: product.productId,
                },
            },
        ];
    }
    else {
        productData = [
            {
                productInfo: {
                    productID: currentProduct.productId,
                    colorCode: currentProduct.colorCode || '',
                    color: currentProduct.color || '',
                },
            },
        ];
    }
    return {
        product: productData,
        directCallRule,
    };
};
export const generateAdobeAnalyticsCompareEvent = (actionType, payload) => {
    let directCallRule = undefined;
    const { product, sourceComponent } = payload;
    switch (actionType) {
        case CompareActionTypes.COMPARE_ADD:
            if (sourceComponent === COMPARE_TRIGGERED_COMPONENT.productDetail ||
                sourceComponent === COMPARE_TRIGGERED_COMPONENT.productLister ||
                sourceComponent === COMPARE_TRIGGERED_COMPONENT.productCompareBox) {
                directCallRule = DirectCallRules.COMPARE_ADD;
            }
            break;
        case CompareActionTypes.COMPARE_REMOVE:
            switch (sourceComponent) {
                case COMPARE_TRIGGERED_COMPONENT.productDetail:
                    directCallRule = DirectCallRules.COMPARE_REMOVE_PDP;
                    break;
                case COMPARE_TRIGGERED_COMPONENT.productLister:
                    directCallRule = DirectCallRules.COMPARE_REMOVE_LISTER;
                    break;
                case COMPARE_TRIGGERED_COMPONENT.productCompareBox:
                    directCallRule = DirectCallRules.COMPARE_REMOVE_COMPARE_BOX;
                    break;
                case COMPARE_TRIGGERED_COMPONENT.comparePage:
                    directCallRule = DirectCallRules.COMPARE_REMOVE_COMPARE_PAGE;
                    break;
                default:
                    break;
            }
            break;
    }
    let productData;
    if (product) {
        productData = [
            {
                attributes: {
                    brand: product.brand,
                    brandValue: product.brandValue || '',
                    buyingGroup: product.buyingGroup || '',
                    gender: product.gender || '',
                    productQualifier: product.productQualifier || '',
                },
                productInfo: {
                    productID: product.id,
                },
            },
        ];
    }
    if (productData) {
        return {
            directCallRule,
            product: productData,
        };
    }
    return {};
};
export const generateAdobeAnalyticsOpenComparePage = () => ({
    directCallRule: DirectCallRules.COMPARE_PAGE_OPEN,
});
export const generateAdobeAnalyticsComparePageNoProductDisplayed = () => ({
    directCallRule: DirectCallRules.COMPARE_PAGE_NO_PRODUCT_DISPLAYED,
});
export const generateAdobeAnalyticsComparePageProductDisplayed = () => ({
    directCallRule: DirectCallRules.COMPARE_PAGE_PRODUCT_DISPLAYED,
});
export const generateAdobeAnalyticsComparePageProductDisplayedToggle = (displayed) => displayed
    ? generateAdobeAnalyticsComparePageProductDisplayed()
    : generateAdobeAnalyticsComparePageNoProductDisplayed();
export const generateAdobeAnalyticsOpenComparePopup = () => ({
    directCallRule: DirectCallRules.COMPARE_POPUP_OPEN,
});
export const generateAdobeAnalyticsCloseComparePopup = () => ({
    directCallRule: DirectCallRules.COMPARE_POPUP_CLOSE,
});
export const generateAdobeAnalyticsToggleComparePopup = (prevCompareState) => prevCompareState.collapsed ? generateAdobeAnalyticsOpenComparePopup() : generateAdobeAnalyticsCloseComparePopup();
export const generateAdobeAnalyticsDeliveryPromiseEvent = (action, productState) => {
    let productEvent = generateAdobeAnalyticsProductDetailImpression(productState, DirectCallRules.PRODUCT_SIZE_PICKED);
    if (action.payload.deliveryPromises) {
        const productDeliveryAndStock = action.payload.deliveryPromises
            .map(item => `${item.name}: ${item.deliveryMessage}`)
            .join(' | ');
        productEvent = mergeObjectDeep(productEvent, {
            page: {
                attributes: {
                    productDeliveryAndStock,
                    stockLocation: action.payload.stockLocation,
                },
            },
            event: [
                {
                    eventInfo: {
                        ruleName: DirectCallRules.PRODUCT_SIZE_PICKED,
                        eventAction: DirectCallRules.PRODUCT_SIZE_PICKED,
                    },
                },
            ],
        });
    }
    return productEvent;
};
export const generateAdobeAnalyticsSearchEventProduct = () => ({
    directCallRule: DirectCallRules.SUCCESSFUL_SEARCH_EVENT_PRODUCT,
});
export const generateAdobeAnalyticsSetSearchTerm = (searchTerm) => {
    if (searchTerm && searchTerm !== '') {
        SessionStorageUtil.setItem(NOTHING_FOUND_SESSION_KEY, '1');
        return {
            directCallRule: DirectCallRules.SUCCESSFUL_SEARCH_EVENT_ITEM,
            page: {
                pageInfo: {
                    onsiteSearch: searchTerm,
                },
            },
        };
    }
    return null;
};
export const generateAdobeAnalyticsNewsletterSubscribeEvent = () => ({
    directCallRule: DirectCallRules.NEWSLETTER_OPT_IN,
    engagement: {
        newsletterType: 'email_newsletter',
    },
});
export const generateAdobeAnalyticsSessionInitEvent = (action) => {
    if (userIsLoggedInHelper(action)) {
        return {
            user: {
                attributes: {
                    loginStatus: true,
                    loginType: 'known user',
                },
                profileInfo: {
                    profileID: action.amcUuid,
                },
            },
        };
    }
    else {
        return {
            user: {
                attributes: {
                    loginStatus: false,
                    loginType: 'guest',
                },
            },
        };
    }
};
export const generateAdobeAnalyticsSearchResultItemsCount = (count) => ({
    page: {
        pageInfo: {
            itemCount: count,
        },
    },
});
export const generateAdobeAnalyticsSearchPasses = (searchPass) => ({
    page: {
        pageInfo: {
            searchPass,
        },
    },
});
export const generateAdobeAnalyticsPaymentRequest = (paymentRequest) => ({
    transaction: {
        total: {
            selectedPaymentGroup: paymentRequest.paymentGroup,
            selectedPaymentMethod: paymentRequest.paymentMethod,
        },
    },
});
export const generateAdobeAnalyticsConfirmationPage = () => ({
    page: {
        pageInfo: {
            pageID: 'confirmation',
        },
    },
});
export const generateAdobeAnalyticsCheckout = (pageID, directCallRule) => {
    const pageData = {
        pageInfo: {
            pageID,
        },
    };
    return {
        page: pageData,
        directCallRule,
    };
};
const getCheckoutStep = (step, option = '') => ({
    event: 'checkout',
    ecommerce: {
        checkout: {
            actionField: {
                step,
                option,
            },
        },
    },
});
const getCheckoutOption = (step, option = '') => ({
    event: 'checkoutOption',
    ecommerce: {
        checkout_option: {
            actionField: {
                step,
                option,
            },
        },
    },
});
export const generateGoogleAnalyticsDeliveryStep = (option = '') => getCheckoutStep(3, option); // delivery step -> 3
export const generateGoogleAnalyticsDeliveryStepOption = (option = '') => getCheckoutOption(3, option); // delivery step -> 3
export const generateGoogleAnalyticsPaymentStep = (option = '') => getCheckoutStep(4, option); // payment step -> 4
export const generateGoogleAnalyticsPaymentStepOption = (option = '') => getCheckoutOption(4, option); // payment step -> 4
export const generateAdobeAnalyticsRegistrationStep = () => ({
    directCallRule: DirectCallRules.CHECKOUT,
    page: { pageInfo: { pageID: PageId.REGISTRATION } },
});
export const generateAdobeAnalyticsDeliveryStep = () => ({
    directCallRule: DirectCallRules.CHECKOUT,
    page: { pageInfo: { pageID: PageId.DELIVERY } },
});
export const generateAdobeAnalyticsPaymentStep = () => ({
    directCallRule: DirectCallRules.CHECKOUT,
    page: { pageInfo: { pageID: PageId.PAYMENT } },
});
export const generateAdobeAnalyticsVideoLoaded = (payload) => ({
    directCallRule: DirectCallRules.PRODUCT_DETAILS_VIDEO_LOADED,
    options: { videoID: payload },
});
export const generateAdobeAnalyticsVoucherAdded = (code) => ({
    directCallRule: DirectCallRules.VOUCHER_ADDED,
    digitalData: { transaction: { attributes: { promoCode: code } } },
});
/**
 * @deprecated
 * This method is deprecated and should not be used anymore when setting up new analytics events.
 * Use `generateAdobeAnalyticsEventObjectV2` instead.
 */
export const generateAdobeAnalyticsEventObject = (ruleName, eventAction) => ({
    event: [
        {
            eventInfo: Object.assign({ directCallRule: true, ruleName }, (eventAction ? { eventAction } : {})),
        },
    ],
});
export const generateAdobeAnalyticsEventObjectV2 = (ruleName, eventAction) => ({
    directCallRule: ruleName,
    event: [
        {
            eventInfo: {
                eventAction,
            },
        },
    ],
});
export const generateAdobeAnalyticsFilterWizardEvent = (eventAction, directCallRule, filterWizardCategory) => (Object.assign(Object.assign({}, generateAdobeAnalyticsEventObject(directCallRule, eventAction)), { tools: {
        filterWizardCategory,
    } }));
export const generateAdobeAnalyticsCheckoutType = (checkoutType) => ({
    transaction: { checkoutType },
});
export const generateAdobeAnalyticsCompareProductsFeatures = (products) => ({
    product: products.map(product => ({
        attributes: {
            nbrComparisonFeatures: product.featuresAmount,
        },
        productInfo: {
            productID: product.id,
        },
    })),
});
export const generateAdobeAnalyticsProductSearchPersonalization = (analyticsData) => ({
    page: {
        pageInfo: {
            personalizedSearch: analyticsData.personalized,
            personalizedSearchAttributes: analyticsData.traits,
        },
    },
});
export const generateAdobeAnalyticsShopTheLook = (name) => (Object.assign(Object.assign({}, generateAdobeAnalyticsEventObject(DirectCallRules.SHOP_THE_LOOK)), { component: {
        componentInfo: {
            shopTheLook: name,
        },
    } }));
export const generateAdobeAnalyticsListerSortChangeEvent = (sortValue) => (Object.assign(Object.assign({}, generateAdobeAnalyticsEventObject(DirectCallRules.LISTER_SORT_BY, EventActions.LISTER_SORT_BY)), { page: {
        attributes: {
            sortByFilterValue: sortValue,
        },
    } }));
export const generateAdobeAnalyticsNewsletterPreferencesSubscriptionUpdateEvent = (optInSubscriptionTypes, optOutSubscriptionTypes) => {
    const digitalData = {
        directCallRule: DirectCallRules.NEWSLETTER_UPDATE_SUBSCRIPTIONS,
        engagement: {},
    };
    if (optInSubscriptionTypes) {
        digitalData.engagement = { newsletterOptIn: objectToString(optInSubscriptionTypes) };
    }
    if (optOutSubscriptionTypes) {
        digitalData.engagement = Object.assign(Object.assign({}, digitalData.engagement), { newsletterOptOut: objectToString(optOutSubscriptionTypes) });
    }
    return digitalData;
};
export const generateAdobeAnalyticsNewsletterPreferencesUpdateEvent = (preferences, directCallRule) => ({
    directCallRule,
    engagement: {
        newsletterType: directCallRule === DirectCallRules.NEWSLETTER_PREFERENCES
            ? subscriptionsObjectToString(preferences)
            : objectToString(preferences),
    },
});
export const generateAdobeAnalyticsReturnPortalLogin = (loginType) => (Object.assign(Object.assign({}, generateAdobeAnalyticsEventObject(DirectCallRules.RETURN_PORTAL_LOGIN, ReturnPortalEventActionType.LOGIN)), { user: {
        attributes: {
            loginType,
        },
    }, page: {
        pageInfo: {
            pageID: PageId.RETURN_PORTAL_LOGIN,
        },
    } }));
export const generateAdobeAnalyticsReturnPortalOverview = (hasItems) => isNullOrUndefined(hasItems)
    ? {
        directCallRule: DirectCallRules.RETURN_PRODUCT_OVERVIEW,
        page: { pageInfo: { pageID: PageId.RETURN_PORTAL_ANNOUNCED_RETURNS } },
    }
    : {
        directCallRule: DirectCallRules.RETURN_PRODUCT_OVERVIEW,
        page: {
            attributes: {
                returnProducts: hasItems ? 'available products' : 'no products available',
            },
            pageInfo: { pageID: PageId.RETURN_PORTAL_RETURN_PRODUCTS },
        },
    };
export const generateAdobeAnalyticsReturnPortalEvent = (ruleName, pageID, eventAction, returnMethod) => {
    const dataObj = eventAction
        ? {
            event: [
                {
                    eventInfo: {
                        directCallRule: true,
                        ruleName,
                        eventAction,
                    },
                },
            ],
            page: { pageInfo: { pageID } },
        }
        : {
            directCallRule: ruleName,
            page: { pageInfo: { pageID } },
        };
    if (returnMethod) {
        setIn(dataObj, 'page.attributes.returnMethod', returnMethod);
    }
    return dataObj;
};
export const generateAdobeAnalyticsShowStoreStock = (productState) => {
    var _a, _b;
    return Object.assign(Object.assign({}, (!isNullOrUndefined(productState.product) &&
        !isNullOrUndefined((_a = productState.product) === null || _a === void 0 ? void 0 : _a.productColorVariations) && {
        product: [
            generateAdobeAnalyticsProduct(productState.product, { expertReviews: (_b = productState.expertReview) === null || _b === void 0 ? void 0 : _b.reviews }),
        ],
    })), generateAdobeAnalyticsEventObject(DirectCallRules.SHOW_STORE_STOCK, EventActions.SHOW_STORE_STOCK));
};
export const generateAdobeAnalyticsProductDetailTabOpen = (tabTitle, pdpTemplateName, productState) => (Object.assign({ product: [
        generateAdobeAnalyticsProduct(productState.product, {
            expertReviews: productState.product.expertReviews,
            pdpTemplateName,
        }),
    ], page: {
        pageInfo: {
            productInformation: tabTitle,
        },
    } }, generateAdobeAnalyticsEventObject(DirectCallRules.PRODUCT_DETAIL_TAB_OPEN, EventActions.PRODUCT_DETAIL_TAB_OPEN)));
export const generateAdobeAnalyticsProductDetailInit = (productDetailState, { pdpVersion, pdpTemplateName, hasSizeTable, productInformation, inANutshellAvailability, } = {}) => {
    const mainProduct = generateAdobeAnalyticsProduct(productDetailState.product, {
        expertReviews: productDetailState.product.expertReviews,
        pdpTemplateName,
        sizeGuideAvailabilityState: hasSizeTable
            ? AnalyticsAvailabilityTypes.AVAILABLE
            : AnalyticsAvailabilityTypes.NOT_AVAILABLE,
        inANutshellAvailability,
    });
    return pdpVersion === PDPVersion.V2
        ? {
            product: [mainProduct],
            mainProduct,
            page: {
                pageInfo: {
                    productInformation: productInformation,
                },
            },
            directCallRule: DirectCallRules.PRODUCT_VIEW,
        }
        : generateAdobeAnalyticsProductDetailImpression(productDetailState, DirectCallRules.PRODUCT_VIEW);
};
export const generateAdobeAnalyticsProductDetailSizeGuideClick = (product, pdpTemplateName) => (Object.assign({ product: [generateAdobeAnalyticsProduct(product, { pdpTemplateName })] }, generateAdobeAnalyticsEventObject(DirectCallRules.PRODUCT_DETAIL_SIZE_GUIDE_CLICK, EventActions.PRODUCT_DETAIL_SIZE_GUIDE_CLICK)));
export const generateAdobeAnalyticsProductDetailAddToBasket = ({ product }, { analyticsAdobeTagManagerData, additionalProducts: additionalProductsData, }) => {
    var _a, _b, _c, _d;
    const analyticsData = generateAdobeAnalyticsBasketChangeEvent(analyticsAdobeTagManagerData);
    const ruleName = analyticsData.event[0].eventInfo.ruleName;
    const isProductBundlesAdded = ((_a = analyticsData.page.attributes) === null || _a === void 0 ? void 0 : _a.addCardLocation) === ProductAddToBasketLocations.PRODUCT_BUNDLES ||
        ((_b = analyticsData.page.attributes) === null || _b === void 0 ? void 0 : _b.addCardLocation) === ProductAddToBasketLocations.PRODUCT_BUNDLES_MONETATE;
    const bundleProducts = isProductBundlesAdded
        ? generateAdobeAnalyticsBundleProductList(product.bundles[product.selectedColorId].bundleSlots)
        : undefined;
    const additionalProducts = additionalProductsData
        ? generateAdobeAnalyticsAdditionalProductList(additionalProductsData)
        : undefined;
    const mainProduct = analyticsData.product.shift() || {};
    const mainProductData = mergeObjectDeep(generateAdobeAnalyticsProduct(product, {
        expertReviews: product.expertReviews,
        sizeGuideViewedSate: product.sizeGuideViewed
            ? AnalyticsSizeGuideViewedTypes.VIEWED
            : AnalyticsSizeGuideViewedTypes.NOT_VIEWED,
        bundleProductType: bundleProducts ? 'Main' : undefined,
        additionalProductBundle: additionalProducts ? 'Main' : undefined,
    }), mainProduct);
    const bundleProductData = analyticsData.product.map(tmpProduct => {
        const linkedBundleProduct = bundleProducts === null || bundleProducts === void 0 ? void 0 : bundleProducts.find(item => parseInt(tmpProduct.productInfo.productID, 10) === item.productInfo.productID);
        return linkedBundleProduct
            ? mergeObjectDeep(tmpProduct, {
                attributes: {
                    bundleProductType: linkedBundleProduct.attributes.bundleProductType,
                },
            })
            : tmpProduct;
    });
    const productData = [mainProductData, ...bundleProductData];
    return Object.assign(Object.assign({}, analyticsData), { product: productData, mainProduct: mainProductData, additionalProduct: additionalProducts
            ? analyticsData.product
                .map(tmpProduct => {
                const linkedAdditionalProduct = additionalProducts === null || additionalProducts === void 0 ? void 0 : additionalProducts.find(item => parseInt(tmpProduct.productInfo.productID, 10) === item.productInfo.productID);
                return linkedAdditionalProduct
                    ? mergeObjectDeep(tmpProduct, {
                        attributes: Object.assign({}, (linkedAdditionalProduct && {
                            additionalProductBundle: linkedAdditionalProduct.attributes.additionalProductBundle,
                        })),
                    })
                    : undefined;
            })
                .filter(Boolean)
            : [], bundleProduct: bundleProducts
            ? analyticsData.product
                .map(tmpProduct => {
                const linkedBundleProduct = bundleProducts === null || bundleProducts === void 0 ? void 0 : bundleProducts.find(item => parseInt(tmpProduct.productInfo.productID, 10) === item.productInfo.productID);
                return linkedBundleProduct
                    ? mergeObjectDeep(tmpProduct, {
                        attributes: {
                            bundleProductType: linkedBundleProduct.attributes.bundleProductType,
                        },
                    })
                    : undefined;
            })
                .filter(Boolean)
            : [], event: [
            {
                eventInfo: {
                    directCallRule: true,
                    ruleName,
                    eventAction: ruleName === DirectCallRules.SC_OPEN
                        ? EventActions.PRODUCT_DETAIL_ADD_TO_EMPTY_BASKET
                        : EventActions.PRODUCT_DETAIL_ADD_TO_BASKET,
                },
            },
        ], page: {
            attributes: Object.assign(Object.assign({}, (Boolean((_c = analyticsData.page) === null || _c === void 0 ? void 0 : _c.attributes) && analyticsData.page.attributes)), { addCardLocation: isProductBundlesAdded
                    ? (_d = analyticsData.page.attributes) === null || _d === void 0 ? void 0 : _d.addCardLocation
                    : ProductAddToBasketLocations.PRODUCT_PAGE }),
        } });
};
export const generateAdobeAnalyticsListerResults = (itemCount) => (Object.assign(Object.assign({}, generateAdobeAnalyticsEventObject(DirectCallRules.PRODUCT_LIST_PAGE)), { page: {
        pageInfo: {
            itemCount,
        },
    } }));
export const generateAdobeAnalyticsListerHasCategoryHierarchyTree = (categoryHierarchyTree) => ({
    page: {
        attributes: {
            categoryTree: (categoryHierarchyTree === null || categoryHierarchyTree === void 0 ? void 0 : categoryHierarchyTree.categoryPath)
                ? AnalyticsAvailabilityTypes.AVAILABLE
                : AnalyticsAvailabilityTypes.NOT_AVAILABLE,
        },
    },
});
export const generateGoogleAnalyticsListerViewProductListEvent = ({ items, event, currency, searchTerm, }) => (Object.assign(Object.assign({ event }, (Boolean(searchTerm) && event === Events.VIEW_SEARCH_RESULTS && { search_term: searchTerm })), { ecommerce: {
        currencyCode: getCurrencyISOCode(currency),
        impressions: convertListerItemToProductImpression(items, { withQuantity: true, withPosition: true }),
    } }));
export const generateGoogleCustomerIdentityAction = (identity, customerInfo) => (Object.assign({ event: 'custIdentify', visIdentity: identity }, (customerInfo && { custInfo: customerInfo })));
export const generateAdobeAnalyticsBundleDealsViewedObject = (product, colorId) => ({
    product: [
        generateAdobeAnalyticsProduct(product, {
            expertReviews: product.expertReviews,
            bundleProductType: 'Main',
        }),
        ...generateAdobeAnalyticsBundleProductList(product.bundles[colorId].bundleSlots),
    ],
    mainProduct: generateAdobeAnalyticsProduct(product, {
        expertReviews: product.expertReviews,
        bundleProductType: 'Main',
    }),
    bundleProduct: generateAdobeAnalyticsBundleProductList(product.bundles[colorId].bundleSlots),
    event: [
        {
            eventInfo: {
                eventAction: EventActions.PRODUCT_BUNDLE_VIEW,
            },
        },
    ],
    directCallRule: DirectCallRules.BUNDLE_DEALS_VIEW,
});
export const generateAdobeAnalyticsNotifyMeObject = (product) => (Object.assign(Object.assign({}, generateAdobeAnalyticsEventObject(DirectCallRules.NOTIFY_ME, EventActions.NOTIFY_ME)), { product: [generateAdobeAnalyticsProduct(product, {})] }));
export const generateAdobeAnalyticsFaqQuestionExpand = (title) => (Object.assign(Object.assign({}, generateAdobeAnalyticsEventObject(DirectCallRules.FAQ_QUESTION_EXPAND, EventActions.FAQ_QUESTION_CLICK_OPEN)), { page: {
        pageInfo: {
            faqQuestion: title,
        },
    } }));
export const generateGoogleAnalyticsPaybackEnrichmentChanged = ({ hasChanged, }) => ({
    event: 'shoppingbudget',
    actionField: 'success',
    label: hasChanged ? 'withenrichment' : 'withoutenrichment',
});
export const generateAdobeAnalyticsEnrichmentChanged = ({ hasChanged, }) => ({
    directCallRule: DirectCallRules.SHOPPING_BUDGET,
    page: {
        attributes: {
            enrichmentState: hasChanged ? 'withenrichment' : 'withoutenrichment',
        },
    },
});
export const generateGoogleAnalyticsPaybackError = ({ analyticsError }) => ({
    event: 'shoppingbudget',
    actionField: 'nosuccess',
    label: analyticsError,
});
export const generateAdobeAnalyticsPaybackError = ({ analyticsError }) => ({
    directCallRule: DirectCallRules.SHOPPING_BUDGET,
    page: {
        attributes: {
            errorMessage: analyticsError,
        },
    },
});
export const generateAdobeAnalyticsProductGroupViewed = ({ componentName, componentType, products, }) => (Object.assign(Object.assign(Object.assign({}, generateAdobeAnalyticsEventObject(DirectCallRules.PRODUCT_GROUP_VIEWED, EventActions.PRODUCT_GROUP_VIEWED)), (products.length
    ? { product: products.map((product, index) => generateProductGroupImpression(product, index + 1)) }
    : {})), { component: {
        componentInfo: {
            componentName,
            componentType,
        },
    } }));
export const generateAdobeAnalyticsProductGroupItemClick = ({ componentName, componentType, productTilePosition, product, }) => (Object.assign(Object.assign({}, generateAdobeAnalyticsEventObject(DirectCallRules.PRODUCT_GROUP_ITEM_CLICK, EventActions.PRODUCT_GROUP_ITEM_CLICK)), { product: [generateProductGroupImpression(product, productTilePosition)], component: {
        componentInfo: {
            componentName,
            componentType,
        },
    } }));
export const generateProductCarouselImageView = (uniqueImgId) => (Object.assign(Object.assign({}, generateAdobeAnalyticsEventObjectV2(DirectCallRules.PDP_CAROUSEL_IMAGE_VIEW, EventActions.PDP_CAROUSEL_IMAGE_VIEW)), { uniqueImgID: uniqueImgId }));
export const generateAdobeAnalyticsProductVariantClick = () => generateAdobeAnalyticsEventObject(DirectCallRules.PRODUCT_VARIANT_CLICK, EventActions.PRODUCT_VARIANT_CLICK);
export const generateAdobeAnalyticsProductVideoLoaded = (payload) => generateAdobeAnalyticsVideoLoaded(payload);
export const generateAdobeAnalyticApplePayButtonInitialInViewport = (payload) => ({
    component: payload,
});
export const generateAdobeAnalyticApplePayButtonImpression = (payload) => (Object.assign(Object.assign({}, generateAdobeAnalyticsEventObjectV2(DirectCallRules.APPLE_PAY_BUTTON_IMPRESSION, EventActions.APPLE_PAY_BUTTON_IMPRESSION)), { component: payload }));
export const generateAdobeAnalyticsGiftVoucherInit = (product) => ({
    product: [generateAdobeAnalyticsProduct(product, {})],
    directCallRule: DirectCallRules.PRODUCT_VIEW,
});
export const generateBasketReminderNotificationViewed = () => (Object.assign({}, generateAdobeAnalyticsEventObjectV2(DirectCallRules.BASKET_REMINDER_NOTIFICATION_VIEW, EventActions.BASKET_REMINDER_NOTIFICATION_VIEW)));
export const generateBasketReminderNotificationEvent = (payload) => (Object.assign({}, generateAdobeAnalyticsEventObjectV2(DirectCallRules.BASKET_REMINDER_NOTIFICATION_CLICK, payload)));
export const generateAdobeAnalyticsAdditionalProductSelector = (payload, productDetailState) => (Object.assign(Object.assign({}, generateAdobeAnalyticsEventObjectV2(DirectCallRules.ADDITIONAL_PRODUCT_SELECTOR_VIEW, EventActions.ADDITIONAL_PRODUCT_SELECTOR_VIEW)), { mainProduct: generateAdobeAnalyticsProduct(productDetailState.product, {
        expertReviews: productDetailState.product.expertReviews,
    }), additionalProduct: generateAdobeAnalyticsAdditionalProductList(payload) }));
